import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight } from 'lucide-react';
import ProductCard from '../components/products/ProductCard';
import api from '../lib/api';

const Categorias = () => {
  const [categories, setCategories] = useState([]);
  const [productsPerCategory, setProductsPerCategory] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategoriesWithProducts();
  }, []);

  const fetchCategoriesWithProducts = async () => {
    setLoading(true);
    try {
      // Buscar categorias
      const categoriesResponse = await api.get('/categories');
      const categoriesData = categoriesResponse.data;
      setCategories(categoriesData);

      // Buscar produtos para cada categoria
      const productsData = {};
      for (const category of categoriesData) {
        try {
          const productsResponse = await api.get(`/products?category_id=${category.id}&limit=3`);
          productsData[category.id] = productsResponse.data;
        } catch (error) {
          console.error(`Erro ao buscar produtos da categoria ${category.name}:`, error);
          productsData[category.id] = [];
        }
      }
      setProductsPerCategory(productsData);
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 md:pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-20 md:pt-24">
      <div className="container mx-auto px-4 max-w-7xl py-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Categorias
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Explore ofertas por categoria e encontre exatamente o que você procura
          </p>
        </div>

        {/* Categories Grid */}
        <div className="space-y-16">
          {categories.map((category) => {
            const products = productsPerCategory[category.id] || [];
            
            if (products.length === 0) return null;

            return (
              <div key={category.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                {/* Category Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-50 rounded-2xl flex items-center justify-center text-4xl">
                      {category.icon || '📦'}
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-bold text-slate-900">
                        {category.name}
                      </h2>
                      <p className="text-slate-600 text-sm">
                        {category.description || 'Melhores ofertas desta categoria'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/ofertas?category=${category.id}`}
                    className="hidden md:flex items-center gap-2 text-orange-600 hover:text-orange-700 font-semibold transition-colors group"
                  >
                    Ver todas
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Top 3 Products */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.slice(0, 3).map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Mobile "Ver todas" */}
                <div className="md:hidden mt-6">
                  <Link
                    to={`/ofertas?category=${category.id}`}
                    className="flex items-center justify-center gap-2 w-full py-3 text-orange-600 hover:text-orange-700 font-semibold transition-colors border-2 border-orange-200 hover:border-orange-300 rounded-lg"
                  >
                    Ver todas de {category.name}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {categories.length === 0 && (
          <div className="text-center py-20">
            <p className="text-slate-600 text-lg">
              Nenhuma categoria disponível no momento
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Categorias;
